/**
 * A simulated ELM327 plus the car behind it.
 *
 * This exists so the entire application can be developed, demonstrated and
 * regression-tested without a vehicle. It speaks the real command set, honours
 * the AT configuration, reports a believable supported-PID map for either fuel
 * type, and injects a round-trip latency close to what a cheap BLE clone gives
 * you -- so the scheduler is exercised under realistic throughput, not an
 * idealised one.
 */
import type { Transmission, Transport } from '../types.js';

export type DrivingStyle = 'calm' | 'normal' | 'aggressive';
export type CycleName = 'urban' | 'mixed' | 'motorway' | 'idle';

export interface SimulatorOptions {
  fuelType?: 'petrol' | 'diesel';
  /** Mean adapter round-trip latency in ms. Real clones sit around 20-60. */
  latencyMs?: number;
  jitterMs?: number;
  style?: DrivingStyle;
  cycle?: CycleName;
  /** Emulate a clone that rejects multi-PID requests. */
  supportsBatch?: boolean;
  /**
   * Emulate the ELM327 v1.5 clone fault seen in the field: when the expected
   * responses hint is appended, a reply longer than one CAN frame never
   * arrives at all. The adapter stops listening after the first frame, never
   * sends the ISO-TP flow control, and the request dies of timeout in silence.
   */
  brokenMultiFrameWithHint?: boolean;
  /**
   * Emulate an adapter that cannot receive a multi-frame reply at all, hint or
   * no hint. The capability probe should notice and fall back to packing
   * batches that fit in a single frame.
   */
  brokenMultiFrame?: boolean;
  /**
   * PIDs this car does not implement, dropped from the support bitmap on top
   * of the fuel-type set. Real cars are missing things the app assumes: no
   * throttle PID and no oil temperature is a perfectly ordinary combination.
   */
  omitPids?: number[];
  /**
   * PIDs that take the whole request down in silence rather than answering.
   *
   * This is what an ECU commonly does when a multi-PID request names something
   * it does not implement: no negative response, no partial reply, nothing at
   * all, so every PID that shared the request is lost with it.
   */
  silentPids?: number[];
  /**
   * Drivetrain coupling. 'auto' models a torque converter, which lets the
   * engine run ahead of the gearbox until it locks up -- the reason engine
   * speed over road speed is not a clean constant on most automatics.
   */
  transmission?: Transmission;
  /** Seed a fault so the diagnostics screen has something to show. */
  dtcs?: string[];
  /**
   * The VIN to report, to play a particular car; null for a car that does not
   * answer the VIN request at all. Defaults to one per fuel type.
   */
  vin?: string | null;
  /**
   * Seconds to fast-forward into the drive cycle at connect. Every cycle
   * starts from a standstill, which is realistic but tedious to watch, so the
   * UI demo and the tests jump straight to the moving part.
   */
  startPhase?: number;
}

interface CarState {
  t: number;
  speed: number; // km/h
  rpm: number;
  gear: number;
  throttle: number; // 0..100
  pedal: number;
  coolant: number;
  oil: number;
  iat: number;
  ambient: number;
  map: number; // kPa absolute
  baro: number;
  fuelLevel: number; // %
  runTime: number;
  odometer: number;
  targetSpeed: number;
  stftB1: number;
  ltftB1: number;
  lambda: number;
  timingAdvance: number;
  egt: number;
  dpfDelta: number;
  railPressure: number;
  batteryV: number;
  distanceSinceClear: number;
}

/** Gear ratios expressed as engine rpm per km/h, including final drive. */
const GEAR_RPM_PER_KPH = [0, 96, 55, 37, 27, 22, 18.5];

// Torque converter. Lock-up needs a high enough gear, enough road speed and a
// light enough throttle; below that the engine runs ahead of the gearbox.
const TCC_MIN_GEAR = 3;
const TCC_MIN_KPH = 45;
const TCC_MAX_PEDAL = 55;
/** Slip with the pedal up, and the extra it grows by with the pedal floored. */
const TCC_BASE_SLIP = 0.06;
const TCC_PEDAL_SLIP = 0.3;

const PETROL_PIDS = [
  0x01, 0x03, 0x04, 0x05, 0x06, 0x07, 0x0a, 0x0b, 0x0c, 0x0d, 0x0e, 0x0f, 0x10, 0x11, 0x13,
  0x14, 0x15, 0x1c, 0x1f, 0x21, 0x24, 0x2e, 0x2f, 0x30, 0x31, 0x32, 0x33, 0x42, 0x43, 0x44,
  0x45, 0x46, 0x47, 0x49, 0x4a, 0x4c, 0x4d, 0x4e, 0x51, 0x52, 0x5c, 0x61, 0x62, 0x63, 0x3c,
];

const DIESEL_PIDS = [
  0x01, 0x03, 0x04, 0x05, 0x07, 0x0b, 0x0c, 0x0d, 0x0f, 0x10, 0x11, 0x1f, 0x21, 0x23, 0x2c,
  0x2d, 0x2f, 0x30, 0x31, 0x33, 0x42, 0x43, 0x45, 0x46, 0x47, 0x49, 0x4a, 0x4c, 0x4d, 0x4e,
  0x51, 0x5c, 0x5d, 0x5e, 0x61, 0x62, 0x63, 0x78, 0x7a, 0x7c, 0x9e, 0xa2,
];

export class ObdSimulator implements Transport {
  readonly kind = 'simulator';
  readonly label: string;

  private _connected = false;
  private dataCbs = new Set<(c: Uint8Array) => void>();
  private closeCbs = new Set<(r?: string) => void>();
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();
  private rxBuffer = '';
  private tickHandle: ReturnType<typeof setInterval> | null = null;

  // adapter configuration mirrored from AT commands
  private echo = true;
  private headers = false;
  private spaces = true;

  private readonly opts: Required<SimulatorOptions>;
  private readonly supported: Set<number>;
  private car: CarState;
  private cyclePhase = 0;
  /** Seconds left with the clutch down in a gear change, and the gear it ends in. */
  private shiftTimer = 0;
  private nextGear = 0;

  constructor(options: SimulatorOptions = {}) {
    this.opts = {
      fuelType: options.fuelType ?? 'petrol',
      latencyMs: options.latencyMs ?? 22,
      jitterMs: options.jitterMs ?? 8,
      style: options.style ?? 'normal',
      cycle: options.cycle ?? 'mixed',
      supportsBatch: options.supportsBatch ?? true,
      brokenMultiFrameWithHint: options.brokenMultiFrameWithHint ?? false,
      brokenMultiFrame: options.brokenMultiFrame ?? false,
      transmission: options.transmission ?? 'manual',
      omitPids: options.omitPids ?? [],
      silentPids: options.silentPids ?? [],
      dtcs: options.dtcs ?? ['P0301', 'P0420'],
      startPhase: options.startPhase ?? 0,
      vin: options.vin === undefined
        ? (options.fuelType === 'diesel' ? 'WVWZZZ1KZAW123456' : 'VF1RFB00X12345678')
        : options.vin,
    };
    this.cyclePhase = this.opts.startPhase;
    this.label = 'Simulated ' + this.opts.fuelType + ' vehicle';
    this.supported = new Set(this.opts.fuelType === 'diesel' ? DIESEL_PIDS : PETROL_PIDS);
    for (const pid of this.opts.omitPids) this.supported.delete(pid);
    this.car = this.initialCar();
  }

  // ------------------------------------------------------------- Transport

  get connected(): boolean {
    return this._connected;
  }

  async connect(): Promise<void> {
    this._connected = true;
    this.cyclePhase = this.opts.startPhase;
    this.car = this.initialCar();
    this.shiftTimer = 0;
    this.settleToCycle();
    // 20 Hz physics: fine enough that differentiated channels look real.
    this.tickHandle = setInterval(() => this.tick(0.05), 50);
  }

  async disconnect(): Promise<void> {
    this._connected = false;
    if (this.tickHandle) clearInterval(this.tickHandle);
    this.tickHandle = null;
    for (const cb of this.closeCbs) cb('Simulator stopped');
  }

  onData(cb: (chunk: Uint8Array) => void): () => void {
    this.dataCbs.add(cb);
    return () => this.dataCbs.delete(cb);
  }

  onClose(cb: (reason?: string) => void): () => void {
    this.closeCbs.add(cb);
    return () => this.closeCbs.delete(cb);
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this._connected) throw new Error('Simulator not connected');
    this.rxBuffer += this.decoder.decode(data);
    let idx = this.rxBuffer.indexOf('\r');
    while (idx !== -1) {
      const cmd = this.rxBuffer.slice(0, idx).trim();
      this.rxBuffer = this.rxBuffer.slice(idx + 1);
      if (cmd) void this.handleCommand(cmd);
      idx = this.rxBuffer.indexOf('\r');
    }
  }

  // -------------------------------------------------------- driving controls

  setStyle(style: DrivingStyle): void {
    this.opts.style = style;
  }

  setCycle(cycle: CycleName): void {
    this.opts.cycle = cycle;
    this.cyclePhase = 0;
  }

  get snapshot(): Readonly<CarState> {
    return this.car;
  }

  // ------------------------------------------------------------ command side

  private async handleCommand(cmdRaw: string): Promise<void> {
    const cmd = cmdRaw.replace(/\s+/g, '').toUpperCase();
    const lines: string[] = [];

    if (this.swallowsRequest(cmd)) return; // never answers: the caller times out

    if (cmd.startsWith('AT')) {
      lines.push(...this.handleAt(cmd));
    } else {
      lines.push(...this.handleObd(cmd));
    }

    const delay = this.opts.latencyMs + (Math.random() - 0.5) * 2 * this.opts.jitterMs;
    await sleep(Math.max(1, delay));
    if (!this._connected) return;

    let out = '';
    if (this.echo) out += cmdRaw + '\r';
    for (const l of lines) out += (this.spaces ? spaceHex(l) : l) + '\r';
    out += '\r>';
    this.emit(out);
  }

  /**
   * Reproduce the broken-clone behaviour: silence, not an error.
   *
   * Only mode 01 requests that carry the response-count hint AND would answer
   * with more than one CAN frame are affected, which is exactly the pattern the
   * real adapter showed.
   */
  private swallowsRequest(cmd: string): boolean {
    if (!cmd.startsWith('01')) return false;
    const hasHint = cmd.length % 2 === 1;
    const body = hasHint ? cmd.slice(2, -1) : cmd.slice(2);

    // A PID the ECU will not answer for silences the entire request, however
    // many well-behaved PIDs were asked for alongside it.
    if (this.opts.silentPids.length > 0) {
      for (let i = 0; i + 1 < body.length; i += 2) {
        if (this.opts.silentPids.includes(parseInt(body.substr(i, 2), 16))) return true;
      }
    }

    const always = this.opts.brokenMultiFrame;
    if (!always && !this.opts.brokenMultiFrameWithHint) return false;
    if (!always && !hasHint) return false;

    let bytes = 1;
    for (let i = 0; i + 1 < body.length; i += 2) {
      const data = this.encodePid(parseInt(body.substr(i, 2), 16));
      if (data === null) continue;
      bytes += 1 + data.length / 2;
    }
    return bytes > 7;
  }

  private handleAt(cmd: string): string[] {
    const body = cmd.slice(2);
    if (body === 'Z' || body === 'WS') {
      this.echo = true;
      this.headers = false;
      this.spaces = true;
      return ['ELM327 v1.5'];
    }
    if (body === 'I') return ['ELM327 v1.5'];
    if (body === 'E0') { this.echo = false; return ['OK']; }
    if (body === 'E1') { this.echo = true; return ['OK']; }
    if (body === 'H0') { this.headers = false; return ['OK']; }
    if (body === 'H1') { this.headers = true; return ['OK']; }
    if (body === 'S0') { this.spaces = false; return ['OK']; }
    if (body === 'S1') { this.spaces = true; return ['OK']; }
    if (body === 'L0' || body === 'L1') return ['OK'];
    if (body.startsWith('AT')) return ['OK'];
    if (body.startsWith('ST')) return ['OK'];
    if (body.startsWith('SP')) return ['OK'];
    if (body === 'DPN') return ['6'];
    if (body === 'DP') return ['ISO 15765-4 (CAN 11/500)'];
    if (body === 'RV') return [this.car.batteryV.toFixed(1) + 'V'];
    return ['OK'];
  }

  private handleObd(cmd: string): string[] {
    // A trailing odd digit is the "expected responses" hint, not a PID.
    let body = cmd;
    if (body.length % 2 === 1) body = body.slice(0, -1);

    const mode = body.slice(0, 2);
    const rest = body.slice(2);

    if (mode === '01') return this.serviceCurrentData(rest);
    if (mode === '03') return this.serviceDtcs();
    if (mode === '07') return ['47' + '00'];
    if (mode === '0A') return ['4A' + '00'];
    if (mode === '04') return ['44'];
    if (mode === '09') return this.serviceVehicleInfo(rest);
    if (mode === '02') return this.serviceFreezeFrame(rest);
    return ['NO DATA'];
  }

  private serviceCurrentData(rest: string): string[] {
    const pids: number[] = [];
    for (let i = 0; i + 1 < rest.length; i += 2) pids.push(parseInt(rest.substr(i, 2), 16));
    if (pids.length === 0) return ['NO DATA'];
    if (pids.length > 1 && !this.opts.supportsBatch) return ['?'];

    let payload = '41';
    let any = false;
    for (const pid of pids) {
      const data = this.encodePid(pid);
      if (data === null) continue;
      payload += hex2(pid) + data;
      any = true;
    }
    if (!any) return ['NO DATA'];

    const bytes = payload.length / 2;
    if (bytes <= 7) return [payload];

    // Longer than a single CAN frame, so answer the way a real adapter does:
    // a length header followed by indexed rows.
    const out = [hex3(bytes)];
    const rows = Math.ceil((payload.length - 12) / 14) + 1;
    out.push('0:' + payload.slice(0, 12));
    for (let i = 1; i < rows; i++) {
      out.push(String(i) + ':' + payload.slice(12 + (i - 1) * 14, 12 + i * 14));
    }
    return out;
  }

  private serviceDtcs(): string[] {
    const codes = this.opts.dtcs;
    if (codes.length === 0) return ['43' + '00'];
    let payload = '43' + hex2(codes.length);
    for (const c of codes) payload += encodeDtcHex(c);
    return [payload];
  }

  private serviceVehicleInfo(rest: string): string[] {
    if (rest === '00') return ['4900' + '55000000'];
    if (rest === '02') {
      const vin = this.opts.vin;
      if (vin === null) return ['NO DATA'];
      let hexVin = '';
      for (const ch of vin) hexVin += ch.charCodeAt(0).toString(16).padStart(2, '0').toUpperCase();
      const payload = '490201' + hexVin;
      const bytes = payload.length / 2;
      const out = [hex3(bytes), '0:' + payload.slice(0, 12)];
      const rows = Math.ceil((payload.length - 12) / 14);
      for (let i = 0; i < rows; i++) {
        out.push(String(i + 1) + ':' + payload.slice(12 + i * 14, 12 + (i + 1) * 14));
      }
      return out;
    }
    return ['NO DATA'];
  }

  private serviceFreezeFrame(rest: string): string[] {
    const pid = parseInt(rest.slice(0, 2), 16);
    const data = this.encodePid(pid);
    if (data === null) return ['NO DATA'];
    return ['42' + hex2(pid) + '00' + data];
  }

  // --------------------------------------------------------- PID encoding

  /** Returns hex data bytes for a PID, or null if this car does not have it. */
  private encodePid(pid: number): string | null {
    const c = this.car;

    // Support bitmaps are always answerable.
    if (pid === 0x00 || pid === 0x20 || pid === 0x40 || pid === 0x60 || pid === 0x80 || pid === 0xa0) {
      return this.supportBitmap(pid);
    }
    if (!this.supported.has(pid)) return null;

    const pctByte = (v: number): string => hex2(clamp(Math.round((v * 255) / 100), 0, 255));
    const trimByte = (v: number): string => hex2(clamp(Math.round((v + 100) * 1.28), 0, 255));
    const temp = (v: number): string => hex2(clamp(Math.round(v + 40), 0, 255));

    switch (pid) {
      case 0x01: {
        // MIL on with the stored codes counted; byte B bit 3 marks a diesel,
        // which changes the meaning of the non-continuous monitors.
        const codes = Math.min(this.opts.dtcs.length, 0x7f);
        const status = hex2((codes > 0 ? 0x80 : 0) | codes);
        return this.opts.fuelType === 'diesel'
          ? status + '0F' + 'C3' + '40' // NMHC, NOx, particulate filter, EGR; filter test incomplete
          : status + '07' + 'E5' + '04'; // catalyst, evap, O2, heater, EGR; evap incomplete
      }
      case 0x03: return c.coolant > 60 ? '0200' : '0100';
      case 0x04: return pctByte(this.engineLoad());
      case 0x05: return temp(c.coolant);
      case 0x06: return trimByte(c.stftB1);
      case 0x07: return trimByte(c.ltftB1);
      case 0x0a: return hex2(clamp(Math.round(380 / 3), 0, 255));
      case 0x0b: return hex2(clamp(Math.round(c.map), 0, 255));
      case 0x0c: return hex4(clamp(Math.round(c.rpm * 4), 0, 65535));
      case 0x0d: return hex2(clamp(Math.round(c.speed), 0, 255));
      case 0x0e: return hex2(clamp(Math.round((c.timingAdvance + 64) * 2), 0, 255));
      case 0x0f: return temp(c.iat);
      case 0x10: return hex4(clamp(Math.round(this.maf() * 100), 0, 65535));
      case 0x11: return pctByte(c.throttle * 0.8 + 12);
      case 0x13: return '03';
      case 0x14:
      case 0x15: return hex2(clamp(Math.round(this.o2Voltage() * 200), 0, 255)) + trimByte(c.stftB1);
      case 0x1c: return '06';
      case 0x1f: return hex4(clamp(Math.round(c.runTime), 0, 65535));
      case 0x21: return '0000';
      case 0x23: return hex4(clamp(Math.round(c.railPressure / 10), 0, 65535));
      case 0x24: return hex4(clamp(Math.round((c.lambda * 65536) / 2), 0, 65535)) + hex4(32768);
      case 0x2c: return pctByte(this.egrRate());
      case 0x2d: return trimByte(0);
      case 0x2e: return pctByte(c.speed > 20 ? 18 : 0);
      case 0x2f: return pctByte(c.fuelLevel);
      case 0x30: return '14';
      case 0x31: return hex4(clamp(Math.round(c.distanceSinceClear), 0, 65535));
      case 0x32: return hex4(32768);
      case 0x33: return hex2(Math.round(c.baro));
      case 0x3c: return hex4(clamp(Math.round((this.catTemp() + 40) * 10), 0, 65535));
      case 0x42: return hex4(clamp(Math.round(c.batteryV * 1000), 0, 65535));
      case 0x43: return hex4(clamp(Math.round((this.absoluteLoad() * 255) / 100), 0, 65535));
      case 0x44: return hex4(clamp(Math.round((c.lambda * 65536) / 2), 0, 65535));
      case 0x45: return pctByte(c.throttle);
      case 0x46: return temp(c.ambient);
      case 0x47: return pctByte(c.throttle * 0.8 + 10);
      // Pedal sensors are offset like real ones, so a released pedal is not 0%.
      case 0x49: return pctByte(15 + c.pedal * 0.66);
      case 0x4a: return pctByte(7.5 + c.pedal * 0.33);
      case 0x4c: return pctByte(c.throttle);
      case 0x4d: return '0000';
      case 0x4e: return hex4(clamp(Math.round(c.runTime / 60) + 400, 0, 65535));
      case 0x51: return hex2(this.opts.fuelType === 'diesel' ? 4 : 1);
      case 0x52: return pctByte(5);
      case 0x5c: return temp(c.oil);
      case 0x5d: return hex4(clamp(Math.round((this.injectionTiming() + 210) * 128), 0, 65535));
      case 0x5e: return hex4(clamp(Math.round(this.fuelRateLph() * 20), 0, 65535));
      case 0x61: return hex2(clamp(Math.round(this.torquePct() + 125), 0, 255));
      case 0x62: return hex2(clamp(Math.round(this.torquePct() + 125), 0, 255));
      case 0x63: return hex4(this.opts.fuelType === 'diesel' ? 320 : 240);
      case 0x78: return '03' + hex4(Math.round((c.egt + 40) * 10)) + hex4(Math.round((c.egt * 0.9 + 40) * 10)) + '0000' + '0000';
      case 0x7a: return '07' + hex4(clamp(Math.round((c.dpfDelta * 0.6 + 8192) * 32), 0, 65535))
        + hex4(clamp(Math.round((c.dpfDelta * 0.2 + 8192) * 32), 0, 65535))
        + hex4(clamp(Math.round((c.dpfDelta + 8192) * 32), 0, 65535));
      case 0x7c: return '03' + hex4(Math.round((c.egt * 0.8 + 40) * 10)) + hex4(Math.round((c.egt * 0.7 + 40) * 10)) + '0000' + '0000';
      case 0x9e: return hex4(clamp(Math.round(this.maf() * 3.6 * 10), 0, 65535));
      case 0xa2: return hex4(clamp(Math.round(this.mgPerStroke() * 32), 0, 65535));
      default: return null;
    }
  }

  private supportBitmap(base: number): string {
    let bits = 0;
    for (let i = 1; i <= 32; i++) {
      const pid = base + i;
      if (this.supported.has(pid)) bits |= 0x80000000 >>> (i - 1);
    }
    // Bit 0 signals that the next bitmap exists.
    const hasNext = [...this.supported].some((p) => p > base + 32);
    if (hasNext && base < 0xa0) bits |= 1;
    return (bits >>> 0).toString(16).padStart(8, '0').toUpperCase();
  }

  // ------------------------------------------------------------ vehicle model

  private initialCar(): CarState {
    return {
      t: 0, speed: 0, rpm: 780, gear: 0, throttle: 0, pedal: 0,
      coolant: 18, oil: 18, iat: 20, ambient: 16, map: 32, baro: 101,
      fuelLevel: 62, runTime: 0, odometer: 84213, targetSpeed: 0,
      stftB1: 0, ltftB1: 2.3, lambda: 1, timingAdvance: 10,
      egt: 120, dpfDelta: 3, railPressure: 30000, batteryV: 14.1,
      distanceSinceClear: 1240,
    };
  }

  /**
   * Start already travelling at the cycle's current target rather than from a
   * standstill. Without this, fast-forwarding the cycle still begins with a
   * full-throttle launch, which is exactly the transient a cruise test is
   * trying to avoid -- and makes the UI demo open on a hard acceleration.
   */
  private settleToCycle(): void {
    const c = this.car;
    const target = this.cycleTarget();
    if (target < 3) return;
    c.speed = target;
    c.targetSpeed = target;
    // Pick the highest gear that keeps the engine above its shift-down point.
    c.gear = 1;
    for (let g = 6; g >= 1; g--) {
      if (target * (GEAR_RPM_PER_KPH[g] ?? 30) > 1400) {
        c.gear = g;
        break;
      }
    }
    c.rpm = Math.max(target * (GEAR_RPM_PER_KPH[c.gear] ?? 30), 800);
    // A car that has been driving is warm, not at ambient.
    c.coolant = 88;
    c.oil = 82;
    c.pedal = 22;
    c.throttle = 22;
  }

  /** Advance the physics by `dt` seconds. */
  private tick(dt: number): void {
    const c = this.car;
    c.t += dt;
    c.runTime += dt;
    this.cyclePhase += dt;

    c.targetSpeed = this.cycleTarget();

    // Driver model: proportional on speed error, with style setting how hard
    // the pedal is allowed to move and how much overshoot is tolerated.
    const style = this.opts.style;
    const gain = style === 'aggressive' ? 3.2 : style === 'calm' ? 0.9 : 1.7;
    const maxRate = style === 'aggressive' ? 240 : style === 'calm' ? 45 : 110;

    const error = c.targetSpeed - c.speed;
    const desiredPedal = clamp(error * gain + (c.targetSpeed > 1 ? 8 : 0), 0, 100);
    const rate = clamp((desiredPedal - c.pedal) / dt, -maxRate, maxRate);
    c.pedal = clamp(c.pedal + rate * dt, 0, 100);
    c.throttle = c.pedal;

    // Longitudinal dynamics, crudely: drive force from pedal, minus drag and
    // rolling resistance, minus braking when we are over the target.
    const mass = 1450;
    // Peak tractive force is sized so full throttle gives about 3.4 m/s2, i.e.
    // 0-100 km/h in roughly nine seconds. Anything weaker and the simulated car
    // physically cannot drive badly enough to exercise the scoring.
    const maxForce = this.opts.fuelType === 'diesel' ? 5400 : 5200;
    const drive = (c.pedal / 100) * maxForce * (c.gear === 0 ? 0 : 1);
    const drag = 0.42 * c.speed * c.speed * 0.0116;
    const rolling = 140;
    let braking = 0;
    if (error < -1.5) {
      // Both the gain and the ceiling vary by style, so a calm driver brakes
      // gently and early while an aggressive one stands on the pedal.
      const brakeGain = style === 'aggressive' ? 900 : style === 'calm' ? 200 : 480;
      const brakeMax = style === 'aggressive' ? 8000 : style === 'calm' ? 2600 : 4800;
      braking = Math.min(-error * brakeGain, brakeMax);
    }
    const accel = (drive - drag - rolling - braking) / mass;
    c.speed = Math.max(0, c.speed + accel * 3.6 * dt);
    c.odometer += (c.speed / 3600) * dt;
    c.distanceSinceClear += (c.speed / 3600) * dt;

    // Gearbox: shift points depend on fuel type and how hard you are driving.
    const upshiftRpm = this.opts.fuelType === 'diesel'
      ? (style === 'aggressive' ? 3600 : 2200)
      : (style === 'aggressive' ? 5200 : 2600);
    const downshiftRpm = this.opts.fuelType === 'diesel' ? 1200 : 1400;

    // A driver declutches to come to a stop, and the clutch is down for a
    // moment in every gear change. Both matter to anything reading rpm/speed.
    const stopping = c.targetSpeed < 1 && c.speed < 20;
    if (this.shiftTimer > 0) {
      this.shiftTimer -= dt;
      if (this.shiftTimer <= 0) c.gear = this.nextGear;
    } else if (stopping || c.speed < 3) {
      // Below walking pace the car is either stopped or pulling away on a
      // slipping clutch. It has to be able to engage first gear here, because
      // drive force is gated on being in gear -- leaving it in neutral until
      // 3 km/h means it can never reach 3 km/h.
      // An automatic never declutches: drive stays engaged at a standstill
      // and the converter absorbs the difference.
      c.gear = this.opts.transmission === 'auto' || (!stopping && c.pedal > 2) ? 1 : 0;
    } else if (c.gear === 0) {
      c.gear = gearFor(c.speed, downshiftRpm);
    } else {
      const rpmNow = c.speed * (GEAR_RPM_PER_KPH[c.gear] ?? 30);
      if (rpmNow > upshiftRpm && c.gear < 6) this.shift(c.gear + 1);
      else if (rpmNow < downshiftRpm && c.gear > 1) this.shift(c.gear - 1);
    }

    const idle = this.opts.fuelType === 'diesel' ? 800 : 760;
    if (c.gear === 0) {
      // Foot off during a change; otherwise the pedal revs a free engine.
      const pedal = this.shiftTimer > 0 ? 0 : c.pedal;
      c.rpm += ((idle + pedal * 22) - c.rpm) * Math.min(1, dt * 4);
    } else if (this.opts.transmission === 'auto') {
      // Torque converter. The turbine turns with the gearbox, the pump with
      // the engine, and fluid between them lets the engine run ahead by an
      // amount that grows with how hard it is being asked to pull and shrinks
      // as road speed builds. Above the lock-up point a clutch ties the two
      // together and the ratio goes as rigid as a manual's. This is why one
      // gear shows a whole spread of rpm-per-km/h rather than a single value.
      const turbine = c.speed * (GEAR_RPM_PER_KPH[c.gear] ?? 30);
      const lockedUp = c.gear >= TCC_MIN_GEAR && c.speed > TCC_MIN_KPH && c.pedal < TCC_MAX_PEDAL;
      const slip = lockedUp ? 0 : TCC_BASE_SLIP + (c.pedal / 100) * TCC_PEDAL_SLIP * Math.exp(-c.speed / 35);
      // At rest the converter stalls: the engine runs up against it and the
      // car creeps. Nothing about that is a gear ratio.
      const stall = idle + c.pedal * 16;
      const target = Math.max(turbine * (1 + slip), c.speed < 8 ? stall : idle);
      c.rpm += (target - c.rpm) * Math.min(1, dt * 7);
    } else {
      const locked = c.speed * (GEAR_RPM_PER_KPH[c.gear] ?? 30);
      // Pulling away, the clutch slips with the engine held above idle until
      // road speed catches up with it.
      const held = c.gear === 1 ? idle + c.pedal * 14 : idle;
      c.rpm += (Math.max(locked, held) - c.rpm) * Math.min(1, dt * 9);
    }

    // Manifold pressure tracks throttle; a turbo diesel builds real boost.
    const load = this.engineLoad();
    const boostCeiling = this.opts.fuelType === 'diesel' ? 175 : 105;
    const targetMap = 28 + (load / 100) * (boostCeiling - 28);
    c.map += (targetMap - c.map) * Math.min(1, dt * 5);

    // Thermal model: warm-up then thermostat regulation.
    const heat = (0.02 + load / 900) * dt * 6;
    const cooling = Math.max(0, c.coolant - 88) * dt * 0.22;
    c.coolant = clamp(c.coolant + heat - cooling, c.ambient, 108);
    c.oil += (c.coolant - 6 - c.oil) * dt * 0.05;
    c.iat = c.ambient + (c.map > 110 ? (c.map - 101) * 0.28 : 0) + load * 0.06;

    c.fuelLevel = Math.max(0, c.fuelLevel - (this.fuelRateLph() / 3600) * dt * (100 / 55));
    c.batteryV = 14.2 - (c.rpm < 900 ? 0.5 : 0) - Math.random() * 0.06;

    // Closed-loop fuelling wobble, and enrichment when you floor it.
    if (this.opts.fuelType === 'petrol') {
      c.lambda = load > 82 ? 0.86 + Math.random() * 0.02 : 1 + Math.sin(c.t * 6) * 0.035;
      c.stftB1 = Math.sin(c.t * 5.5) * 4.2 + (load > 82 ? -6 : 0);
      c.timingAdvance = clamp(34 - load * 0.24 + Math.sin(c.t * 2) * 1.5, -6, 40);
    } else {
      c.lambda = clamp(1.15 + (1 - load / 100) * 2.6, 1.1, 5);
      c.stftB1 = 0;
      c.timingAdvance = 0;
      c.railPressure = 25000 + (load / 100) * 155000;
      c.egt = clamp(140 + load * 5.4 + c.rpm * 0.02, 120, 780);
      // Soot slowly loads the filter and burns off on a hot motorway run.
      const regen = c.egt > 520 ? dt * 0.09 : 0;
      c.dpfDelta = clamp(c.dpfDelta + dt * 0.0016 * (load / 50) - regen, 1.5, 42);
    }
  }

  private shift(to: number): void {
    this.nextGear = to;
    this.car.gear = 0;
    this.shiftTimer = 0.4;
  }

  /** Target speed for the selected drive cycle, in km/h. */
  private cycleTarget(): number {
    const p = this.cyclePhase;
    switch (this.opts.cycle) {
      case 'idle':
        return 0;
      case 'urban': {
        // Repeating 90 s stop-start loop.
        const x = p % 90;
        if (x < 12) return 0;
        if (x < 30) return 38;
        if (x < 40) return 18;
        if (x < 58) return 50;
        if (x < 66) return 0;
        if (x < 82) return 42;
        return 12;
      }
      case 'motorway': {
        const x = p % 240;
        if (x < 25) return 60 + x * 2.4;
        if (x < 180) return 118 + Math.sin(x / 18) * 7;
        if (x < 210) return 95;
        return 120;
      }
      default: {
        const x = p % 300;
        if (x < 20) return 0;
        if (x < 60) return 45;
        if (x < 95) return 20;
        if (x < 140) return 75;
        if (x < 200) return 110;
        if (x < 240) return 80;
        if (x < 275) return 35;
        return 0;
      }
    }
  }

  private engineLoad(): number {
    const c = this.car;
    const rpmFactor = clamp(c.rpm / 3200, 0.25, 1.4);
    return clamp(c.pedal * 0.82 * rpmFactor + (c.gear > 0 ? 8 : 3), 0, 100);
  }

  private absoluteLoad(): number {
    return clamp(this.engineLoad() * 0.92, 0, 100);
  }

  private maf(): number {
    const c = this.car;
    // Speed-density, the same relation the app uses in reverse.
    const displacement = this.opts.fuelType === 'diesel' ? 1.968 : 1.598;
    const ve = 0.86;
    const cycles = c.rpm / 120;
    const density = (c.map * 1000) / (287.05 * (c.iat + 273.15));
    return Math.max(0.5, cycles * (displacement / 1000) * ve * density * 1000);
  }

  private fuelRateLph(): number {
    const afr = this.opts.fuelType === 'diesel' ? 14.5 : 14.7;
    const density = this.opts.fuelType === 'diesel' ? 832 : 745;
    const gPerSec = this.maf() / (this.car.lambda * afr);
    return (gPerSec * 3600) / density;
  }

  private mgPerStroke(): number {
    const c = this.car;
    const density = 832;
    const gPerSec = (this.fuelRateLph() * density) / 3600;
    const strokesPerSec = (c.rpm / 120) * 4;
    return strokesPerSec > 0 ? (gPerSec * 1000) / strokesPerSec : 0;
  }

  private torquePct(): number {
    return clamp(this.engineLoad() * 0.9 - 6, -20, 100);
  }

  private o2Voltage(): number {
    return this.car.lambda < 1 ? 0.78 + Math.random() * 0.08 : 0.12 + Math.sin(this.car.t * 6) * 0.35 + 0.35;
  }

  private catTemp(): number {
    return clamp(180 + this.engineLoad() * 5.2, 120, 820);
  }

  private egrRate(): number {
    const load = this.engineLoad();
    return clamp(load < 60 ? 28 - load * 0.2 : 0, 0, 40);
  }

  private injectionTiming(): number {
    return clamp(-2 - this.engineLoad() * 0.08, -14, 6);
  }

  private emit(text: string): void {
    const bytes = this.encoder.encode(text);
    for (const cb of this.dataCbs) cb(bytes);
  }
}

// --------------------------------------------------------------- utilities

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** The highest gear that keeps the engine above its change-down point. */
function gearFor(kph: number, downshiftRpm: number): number {
  for (let g = 6; g > 1; g--) {
    if (kph * (GEAR_RPM_PER_KPH[g] ?? 30) > downshiftRpm) return g;
  }
  return 1;
}

const hex2 = (v: number): string => Math.round(v).toString(16).padStart(2, '0').toUpperCase();
const hex3 = (v: number): string => Math.round(v).toString(16).padStart(3, '0').toUpperCase();
const hex4 = (v: number): string => Math.round(v).toString(16).padStart(4, '0').toUpperCase();
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const spaceHex = (s: string): string => (/^[0-9A-F]+$/.test(s) ? (s.match(/../g) ?? []).join(' ') : s);

function encodeDtcHex(code: string): string {
  const letters: Record<string, number> = { P: 0, C: 1, B: 2, U: 3 };
  const l = letters[code[0] ?? 'P'] ?? 0;
  const d1 = parseInt(code[1] ?? '0', 10) & 0x03;
  const rest = parseInt(code.slice(2), 16) & 0x0fff;
  return hex2((l << 6) | (d1 << 4) | ((rest >> 8) & 0x0f)) + hex2(rest & 0xff);
}
